import React from 'react';

import { Box, Button, InfoLabel } from '../../../../common/components';
import { Col, Label, Row } from 'react-bootstrap';
import getIncludedResource from '../../../../helpers/getIncludedResource';
import displayAddress from '../../../../helpers/displayAddress';
import {
  deactivateOrganisation,
  reactivateOrganisation,
} from '../../redux/organisationActions';
import displayName from '../../../../helpers/displayName';
import { InternalButtonBar } from '../../../layout/components';

import { connect, useSelector } from 'react-redux';

const convertToUrl = (link) => {
  if (/^(?:https?|link):\/\//.test(link)) {
    return link;
  }

  if (/^(?:http?|link):\/\//.test(link)) {
    return link;
  }

  return 'http://' + link;
};
const OrganisationDetails = ({
  resource,
  onReactivateOrganisation,
  onDeactivateOrganisation,
}) => {
  const { isReactivating, isDeactivating } = useSelector((state) => state.organisation);

  const organisationId = resource.data.id;
  const reactivateOrganisation = () => {
    onReactivateOrganisation(organisationId);
  };

  const deactivateOrganisation = () => {
    onDeactivateOrganisation(organisationId);
  };

  const buttons = () => {
    const actionButtons = [
      {
        permissions: ['organisation.edit'],
        button: (
          <Button
            label="Edit Organisation"
            link={true}
            to={`/admin/organisations/${organisationId}/edit`}
            className="pull-right"
            bsStyle="primary"
            rightIcon="pencil-square-o"
          />
        ),
      },
      {
        statuses: ['inactive'],
        permissions: ['organisation.activate'],
        button: (
          <Button
            className="pull-right"
            label="Reactivate Organisation"
            bsStyle="primary"
            rightIcon="check"
            isLoading={isReactivating}
            handleClick={reactivateOrganisation}
          />
        ),
      },
      {
        statuses: ['active'],
        permissions: ['organisation.deactivate'],
        button: (
          <Button
            className="pull-right"
            label="Deactivate Organisation"
            bsStyle="primary"
            rightIcon="ban"
            isLoading={isDeactivating}
            handleClick={deactivateOrganisation}
          />
        ),
      },
    ];

    return actionButtons.filter((button) => {
      return !(
        button.statuses && button.statuses.indexOf(resourceAttributes.status) === -1
      );
    });
  };

  const resourceAttributes = resource.data.attributes;
  const network = getIncludedResource(resource.data, resource.included, 'network');
  const primaryContacts = getIncludedResource(
    resource.data,
    resource.included,
    'primary_contacts',
  );
  const accountsContacts = getIncludedResource(
    resource.data,
    resource.included,
    'accounts_contacts',
  );
  const organisationType = getIncludedResource(
    resource.data,
    resource.included,
    'organisation_type',
  );
  const legalEntity = getIncludedResource(
    resource.data,
    resource.included,
    'legal_entity_type',
  );
  const standardCommission = getIncludedResource(
    network.data,
    resource.included,
    'standard_commission',
  );

  const channels = getIncludedResource(
    resource.data,
    resource.included,
    'channel_access',
  );

  const address = displayAddress(resource.data.attributes.address);

  const legalEntityCode = legalEntity?.data?.attributes?.code;
  const hasPartners = ['lp', 'llp'].includes(legalEntityCode);
  const website = resourceAttributes?.metadata?.website;

  return (
    <Box>
      <h2 className="resource-name">
        {resourceAttributes.name}
        {!!resourceAttributes?.metadata?.principal_company && (
          <Label className="pull-right" bsStyle="warning">
            Appointed Representative
          </Label>
        )}
      </h2>

      <Row>
        <Col sm={6}>
          <InfoLabel label="Name" value={resourceAttributes.name} labelSize={4} />
          <InfoLabel label="Address" value={address} labelSize={4} />
          <InfoLabel
            label="Phone Number"
            value={resourceAttributes.phone1}
            labelSize={4}
          />
          <InfoLabel
            label="Phone Number 2"
            value={resourceAttributes.phone2}
            labelSize={4}
          />
          <InfoLabel
            label="FCA Number"
            // value={resourceAttributes.regulatory_authority_number}
            value={resourceAttributes.legal_entity_number}
            labelSize={4}
          />
          <InfoLabel
            label="Legal Entity Type"
            value={legalEntity.data.attributes ? legalEntity.data.attributes.name : ''}
            labelSize={4}
          />
          {hasPartners && (
            <InfoLabel
              label="Partners"
              value={resourceAttributes?.metadata?.partners?.map((partner, i) => {
                return i > 0 ? ', ' + partner : partner;
              })}
              labelSize={4}
            />
          )}
          <InfoLabel
            label="Company Number"
            // value={resourceAttributes.legal_entity_number}
            value={resourceAttributes.metadata?.company_registration_number}
            labelSize={4}
          />
        </Col>

        <Col sm={6}>
          <InfoLabel
            label="Organisation Type"
            value={organisationType?.data?.attributes?.name ?? ''}
            labelSize={4}
          />
          {resourceAttributes.organisation_type_name === 'Network' ? (
            <InfoLabel
              label="Network Name"
              value={network?.data?.attributes?.name}
              labelSize={4}
            />
          ) : (
            <InfoLabel
              label="Parent Company"
              value={resourceAttributes?.metadata?.parent_company}
              labelSize={4}
            />
          )}
          {resourceAttributes.organisation_type_name === 'Network' && (
            <InfoLabel
              label="Commission Rate"
              value={`${standardCommission?.data?.attributes?.rate ?? ''}%`}
              labelSize={4}
            />
          )}
          {resourceAttributes.organisation_type_name === 'Broker' && (
            <InfoLabel
              label="Principal Company"
              value={resourceAttributes?.metadata?.principal_company}
              labelSize={4}
            />
          )}
          <InfoLabel
            label="Website"
            value={
              website ? (
                <a href={convertToUrl(website)} target="__blank">
                  {website}
                </a>
              ) : (
                ''
              )
            }
            labelSize={4}
          />

          <InfoLabel
            label="Channel Access"
            value={channels?.map((channel) => channel?.attributes?.name)}
            labelSize={4}
          />
          <InfoLabel
            label="Status"
            value={displayName(resourceAttributes.status)}
            labelSize={4}
          />
          <InfoLabel
            label="Created At"
            date={true}
            value={resourceAttributes.created_at}
            labelSize={4}
          />
          <InfoLabel
            label="Updated At"
            date={true}
            value={resourceAttributes.updated_at}
            labelSize={4}
          />
        </Col>
      </Row>

      {!!primaryContacts?.length && (
        <div>
          <div className="hr" />
          <h2>Primary contacts</h2>
        </div>
      )}

      {Array.isArray(primaryContacts) &&
        primaryContacts.map((contact) => (
          <div key={contact.id}>
            <Row>
              <Col sm={6}>
                <InfoLabel
                  label="Name"
                  value={
                    contact.attributes.first_name + ' ' + contact.attributes.last_name
                  }
                  labelSize={4}
                />
              </Col>
              <Col sm={6}>
                <InfoLabel
                  label="Phone"
                  value={contact.attributes.phone1}
                  labelSize={4}
                />
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <InfoLabel label="Email" value={contact.attributes.email} labelSize={4} />
              </Col>
            </Row>

            <div className="hr transparent" />
          </div>
        ))}

      {!!accountsContacts?.length && (
        <div>
          <div className="hr" />
          <h2>Accounts contacts</h2>
        </div>
      )}

      {Array.isArray(accountsContacts) &&
        accountsContacts.map((contact) => (
          <div key={contact.id}>
            <Row>
              <Col sm={6}>
                <InfoLabel
                  label="Name"
                  value={
                    contact.attributes.first_name + ' ' + contact.attributes.last_name
                  }
                  labelSize={4}
                />
              </Col>
              <Col sm={6}>
                <InfoLabel
                  label="Phone"
                  value={contact.attributes.phone1}
                  labelSize={4}
                />
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <InfoLabel label="Email" value={contact.attributes.email} labelSize={4} />
              </Col>
            </Row>
          </div>
        ))}

      <div className="hr transparent" />
      <Row>
        <Col sm={12}>
          <InternalButtonBar buttons={buttons()} />
        </Col>
      </Row>
    </Box>
  );
};
export default connect(
  null,
  {
    onReactivateOrganisation: reactivateOrganisation,
    onDeactivateOrganisation: deactivateOrganisation,
  },
)(OrganisationDetails);
